import _ from 'lodash';
import { updateSignupOfferingStatusChosen } from '@/api/SignupOfferingsStatusApi';
import BillyApi from '@/api/BillyApi';
import Segment from '@/Segment';
import DDLogs from '@/DDLogs';
import LiorFrenkelAudioCourse from '@/store/modules/signup-offerings/lior-frenkel-audio-course';
import Boost from '@/store/modules/signup-offerings/boost';
import { fillOneOffOfferingDisplayInfo } from '@/store/modules/signup-offerings/oneOff-Offerings';

const state = {
  plansDisplayInfo: {},
  initialized: false,
  signupOfferings: [],
};

const getters = {
  hasSignupOfferings: state => !_.isEmpty(state.plansDisplayInfo),
  offeringCards: state => _
    .chain(state.plansDisplayInfo)
    .values()
    .map(offering => ({
      ...offering.offeringCard,
      name: offering.name,
      description: offering.description,
      id: offering.id,
      startDate: offering.startDate,
      skipPurchaseInfoPage: offering.skipPurchaseInfoPage,
    }))
    .value(),
  purchaseInfo: state => signupOfferingId => {
    const offering = state.plansDisplayInfo[signupOfferingId];
    return {
      ...offering.purchaseInfo,
      name: offering.name,
      description: offering.description,
      startDate: offering.startDate,
    };
  },
};

const actions = {
  async init({ state, getters, dispatch, commit }, { offerIds, featureFlagAvailableOfferings }) {
    if (!state.initialized) {
      commit('setSignupOfferings', [new LiorFrenkelAudioCourse(), new Boost()]);
      const { oneOffOfferings } = await BillyApi.getOfferings();
      await dispatch('getAvailableOneOffOfferings', { offerIds, featureFlagAvailableOfferings, oneOffOfferings });
      const offeringsIds = getters.offeringCards.map(offering => offering.id);
      Segment.trackUserGot('AvailableOfferings_Initialized', { offerings: JSON.stringify(offeringsIds), isOfferIdInQueryParam: offerIds.length > 0 });
      commit('setInitialized', true);
    }
  },
  async updateSignupOfferingStatusChosen() {
    await updateSignupOfferingStatusChosen();
  },

  async getAvailableOneOffOfferings({ commit }, { offerIds, featureFlagAvailableOfferings, oneOffOfferings }) {
    const oneOffOfferingsIds = oneOffOfferings.map(offer => offer.id);
    const calculatedOfferings = await Promise.all(
      state.signupOfferings.map(async offer => {
        const hasOfferInBackend = oneOffOfferingsIds.includes(offer.purchaseInfo().details.offerId);
        return {
          ...offer,
          purchaseInfo: offer.purchaseInfo(),
          isAvailable: hasOfferInBackend && await offer.isAvailable({ offerIds, featureFlagAvailableOfferings }),
          startDate: await offer.startDate(),
        };
      }),
    );
    // If the query parameter includes offer IDs, display only the available offerings corresponding to these IDs.
    if (offerIds.length > 0) {
      const availableSignupOfferings = _.chain(calculatedOfferings)
        .filter(offer => offerIds.includes(offer.id))
        .filter(offer => offer.isAvailable)
        .map(offer => fillOneOffOfferingDisplayInfo(offer, oneOffOfferings))
        .keyBy('id')
        .value();
      if (!availableSignupOfferings) {
        DDLogs.log(`signup offerings with query param ids ${offerIds} not found in the available offerings.`);
        return;
      }
      commit('setPlansDisplayInfo', { offerings: availableSignupOfferings });
    } else {
      const availableSignupOfferings = _.chain(calculatedOfferings)
        .filter(offer => offer.isAvailable)
        .map(offer => fillOneOffOfferingDisplayInfo(offer, oneOffOfferings))
        .keyBy('id')
        .value();
      commit('setPlansDisplayInfo', { offerings: availableSignupOfferings });
    }
  },
};

const mutations = {
  setPlansDisplayInfo(state, { offerings }) {
    state.plansDisplayInfo = offerings;
  },
  setInitialized(state, initialized) {
    state.initialized = initialized;
  },
  setSignupOfferings(state, signupOfferings) {
    state.signupOfferings = signupOfferings;
  },
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
